/**
 * custom-search.js
 * Powers the search panel rendered by snippets/custom-search-bar.liquid
 *
 * Features:
 *  - Open / close panel (toggle button in header, Escape key, backdrop click)
 *  - Debounced Shopify Predictive Search API calls
 *  - Autocomplete suggestions list with full arrow-key navigation
 *  - Product results grid with Tab + arrow-key navigation
 *  - Skeleton loading state
 *  - "Did you mean" typo-correction fallback for zero-result queries
 *  - "Has query" class on panel drives CSS state transitions
 *  - View-all link updated to current query
 *  - Falls back gracefully when Search & Discovery is not installed
 */

(function () {
  'use strict';

  /* ──────────────────────────────────────────────────────────────
     CONSTANTS
  ────────────────────────────────────────────────────────────── */
  var DEBOUNCE_MS      = 180;
  var MIN_CHARS        = 2;
  var MAX_RESULTS      = 8;
  var MAX_AUTOCOMPLETE = 6;
  var MAX_FALLBACK     = 4;

  /* ──────────────────────────────────────────────────────────────
     HELPERS
  ────────────────────────────────────────────────────────────── */
  function $(sel, ctx)  { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function onReady(fn) {
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn)
      : fn();
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(this, args); }, ms);
    };
  }

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    return '$' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function escHtml(str) {
    var d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  function highlightMatch(text, query) {
    if (!query) return escHtml(text);
    var re = new RegExp('(' + query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'gi');
    return escHtml(text).replace(re, '<mark class="csb__autocomplete-match">$1</mark>');
  }

  /* ──────────────────────────────────────────────────────────────
     SHARED PRODUCT CARD BUILDER
     Handles both Predictive Search API shape and REST Products API shape:
       Predictive: featured_image.url, price (dollar string), compare_at_price_max, url
       REST:       images[].src, variants[0].price (dollar string), handle
     Both shapes express price as a decimal dollar string, e.g. "1795.00".
  ────────────────────────────────────────────────────────────── */
  function buildCard(product) {
    var imgSrc = product.featured_image
      ? product.featured_image.url
      : (product.images && product.images[0] ? product.images[0].src : '');
    var imgAlt = (product.featured_image && (product.featured_image.alt || product.title))
      || product.title || '';
    if (imgSrc) imgSrc = imgSrc.replace(/\.(jpg|png|webp|gif)(\?.*)?$/, '_300x300.$1');

    var images  = product.images || [];
    var img2Src = (images[1] && images[1].src)
      ? images[1].src.replace(/\.(jpg|png|webp|gif)(\?.*)?$/, '_300x300.$1')
      : '';

    // Both API shapes give a decimal dollar string (e.g. "1795.00"), never cents
    var priceNum = parseFloat(product.price);
    if (isNaN(priceNum) && product.variants && product.variants[0]) {
      priceNum = parseFloat(product.variants[0].price);
    }
    var price = (!isNaN(priceNum) && priceNum > 0) ? formatMoney(priceNum * 100) : '';

    // parseFloat both sides — comparing the raw strings is lexicographic,
    // so "1200.00" > "980.00" reads as false and hides a genuine sale price
    var compareNum = parseFloat(product.compare_at_price_max);
    var compareAt = (!isNaN(compareNum) && compareNum > priceNum)
      ? formatMoney(compareNum * 100) : '';

    var url = product.url || (product.handle ? '/products/' + product.handle : '#');

    var cardClass = 'csb__product-card' + (img2Src ? ' csb__has-alt' : '');
    var html = '<a href="' + escHtml(url) + '" class="' + cardClass + '">';
    html += '<div class="csb__product-img-wrap">';
    if (imgSrc) {
      html += '<img src="' + escHtml(imgSrc) + '" alt="' + escHtml(imgAlt)
        + '" class="csb__product-img" loading="lazy" width="300" height="300">';
      if (img2Src) {
        html += '<img src="' + escHtml(img2Src) + '" alt="' + escHtml(imgAlt)
          + '" class="csb__product-img-alt" loading="lazy" width="300" height="300">';
      }
    }
    html += '</div>';
    html += '<p class="csb__product-title">' + escHtml(product.title) + '</p>';
    var hidePrice = Array.isArray(product.tags)
      ? product.tags.indexOf('hide-price') !== -1
      : (typeof product.tags === 'string' && product.tags.split(',').some(function(t) { return t.trim() === 'hide-price'; }));
    if (price && !hidePrice) {
      html += '<p class="csb__product-price">';
      if (compareAt) html += '<s style="opacity:.5;margin-right:4px">' + escHtml(compareAt) + '</s>';
      html += escHtml(price) + '</p>';
    }
    html += '</a>';
    return html;
  }

  /* ──────────────────────────────────────────────────────────────
     GRID LAYOUT HELPER
     Groups an array of card elements into rows by matching y-position.
     Used for 2-D arrow-key navigation within the product grid.
  ────────────────────────────────────────────────────────────── */
  function getGridRows(cards) {
    var rows = [], currentRow = [], currentY = null;
    cards.forEach(function (card) {
      var y = Math.round(card.getBoundingClientRect().top);
      if (currentY === null) currentY = y;
      if (Math.abs(y - currentY) > 4) {
        rows.push(currentRow);
        currentRow = [];
        currentY = y;
      }
      currentRow.push(card);
    });
    if (currentRow.length) rows.push(currentRow);
    return rows;
  }

  /* ──────────────────────────────────────────────────────────────
     SEARCH PANEL CONTROLLER
  ────────────────────────────────────────────────────────────── */
  function initSearchPanel(panel) {
    if (!panel) return;

    var backdrop       = $('[data-csh-search-backdrop]');
    var input          = $('[data-csh-search-input]', panel);
    var closeBtn       = $('[data-csh-search-close]', panel);
    var autocompleteEl = $('[data-csh-autocomplete]', panel);
    var skeleton       = $('[data-csh-skeleton]', panel);
    var resultsGrid    = $('[data-csh-results-grid]', panel);
    var noResults      = $('[data-csh-no-results]', panel);
    var noResultsQ     = $('[data-csh-no-results-query]', panel);
    var resultsLabel   = $('[data-csh-results-label]', panel);
    var resultsQ       = $('[data-csh-results-query]', panel);
    var viewAll        = $('[data-csh-view-all]', panel);
    var featuredGrid   = $('[data-csh-featured-grid]', panel);
    // Results state container — toggled by JS AND CSS (.has-query)
    var resultsState   = $('[data-csh-state="results"]', panel);
    // Did-you-mean elements (new)
    var dymWrapper     = $('[data-csh-did-you-mean]', panel);
    var dymBtn         = $('[data-csh-did-you-mean-btn]', panel);
    var dymQueryEl     = $('[data-csh-dym-query]', panel);
    var fallbackGrid   = $('[data-csh-fallback-grid]', panel);

    var currentQuery   = '';
    var currentXhr     = null;
    var isOpen         = false;
    var featuredLabel  = $('[data-csh-featured-label]', panel);

    /* ── OPEN / CLOSE ────────────────────────────────────────── */
    function openPanel() {
      if (isOpen) return;
      isOpen = true;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      if (backdrop) backdrop.classList.add('is-open');
      $$('[data-csh-search-toggle]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'true');
      });
      if (input) requestAnimationFrame(function () { input.focus(); });
      document.body.style.overflow = 'hidden';
    }

    function closePanel() {
      if (!isOpen) return;
      isOpen = false;
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      if (backdrop) backdrop.classList.remove('is-open');
      $$('[data-csh-search-toggle]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'false');
      });
      document.body.style.overflow = '';
      document.dispatchEvent(new CustomEvent('cvs:stop'));
    }

    /* ── TOGGLE BUTTONS ──────────────────────────────────────── */
    document.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-csh-search-toggle]');
      if (toggle) { e.preventDefault(); isOpen ? closePanel() : openPanel(); }
    });
    if (closeBtn) closeBtn.addEventListener('click', closePanel);
    if (backdrop) backdrop.addEventListener('click', closePanel);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) closePanel();
    });

    /* ── INPUT HANDLING ──────────────────────────────────────── */
    if (input) {
      input.addEventListener('input', onInputImmediate);
      input.addEventListener('input', debounce(onInputFetch, DEBOUNCE_MS));
    }

    function onInputImmediate() {
      var q = input ? input.value.trim() : '';
      if (q.length < MIN_CHARS) { resetToDefault(); return; }
      panel.classList.add('has-query');
      if (resultsState) resultsState.hidden = false;
      showSkeleton();
    }

    function onInputFetch() {
      var q = input ? input.value.trim() : '';
      if (q === currentQuery) return;
      currentQuery = q;
      if (q.length < MIN_CHARS) return;
      updateAutoComplete(q);
      fetchResults(q);
    }

    function resetToDefault() {
      panel.classList.remove('has-query');
      if (resultsState) resultsState.hidden = true;
      if (autocompleteEl) autocompleteEl.hidden = true;
      if (viewAll) viewAll.hidden = true;
      hideDym();
      currentQuery = '';
    }

    /* ── AUTOCOMPLETE ────────────────────────────────────────── */
    function updateAutoComplete(q) {
      if (!autocompleteEl) return;
      fetch('/search/suggest.json?q=' + encodeURIComponent(q)
        + '&resources[type]=query&resources[limit]=' + MAX_AUTOCOMPLETE)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var suggestions = (data.resources && data.resources.results
            && data.resources.results.queries) || [];
          renderAutoComplete(suggestions, q);
        })
        .catch(function () { if (autocompleteEl) autocompleteEl.hidden = true; });
    }

    function renderAutoComplete(items, q) {
      if (!autocompleteEl) return;
      if (!items.length) { autocompleteEl.hidden = true; return; }

      var html = '';
      items.slice(0, MAX_AUTOCOMPLETE).forEach(function (item) {
        var text = item.text || '';
        // tabindex="-1": arrow-key navigation only; Tab from the input jumps to product cards
        html += '<li role="option" aria-selected="false">';
        html += '<a href="' + escHtml(item.url || '/search?q=' + encodeURIComponent(text))
          + '" class="csb__autocomplete-item" tabindex="-1">';
        html += '<svg viewBox="0 0 16 16" fill="none" width="12" height="12" aria-hidden="true">'
          + '<circle cx="6.5" cy="6.5" r="4" stroke="currentColor" stroke-width="1.2"/>'
          + '<path d="M10 10l3 3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>'
          + '</svg>';
        html += highlightMatch(text, q);
        html += '</a></li>';
      });
      html += '<li><a href="/search?type=product&q=' + encodeURIComponent(q)
        + '" class="csb__autocomplete-view-all" tabindex="-1">';
      html += 'See all results for "' + escHtml(q) + '"</a></li>';

      autocompleteEl.innerHTML = html;
      autocompleteEl.hidden = false;
    }

    document.addEventListener('click', function (e) {
      if (autocompleteEl && !autocompleteEl.contains(e.target) && e.target !== input) {
        autocompleteEl.hidden = true;
      }
    });

    /* ── PRODUCT RESULTS ─────────────────────────────────────── */
    function showSkeleton() {
      if (skeleton) skeleton.hidden = false;
      if (resultsGrid) resultsGrid.innerHTML = '';
      if (noResults) noResults.hidden = true;
      if (viewAll) viewAll.hidden = true;
      hideDym();
    }

    function hideSkeleton() { if (skeleton) skeleton.hidden = true; }

    function hideDym() {
      if (dymWrapper) dymWrapper.hidden = true;
      if (fallbackGrid) fallbackGrid.innerHTML = '';
    }

    // Filter out private/vault products client-side so we don't append boolean
    // operators to the query string — those operators disable Shopify's
    // prefix/fuzzy matching and break partial-term search (e.g. "water" → "Water Drop").
    var HIDDEN_TAGS = ['the vault'];
    function isPrivate(product) {
      var tags = product.tags;
      var tagList;
      if (Array.isArray(tags)) {
        tagList = tags.map(function (t) { return String(t).trim().toLowerCase(); });
      } else if (typeof tags === 'string') {
        tagList = tags.split(',').map(function (t) { return t.trim().toLowerCase(); });
      } else {
        return false;
      }
      return HIDDEN_TAGS.some(function (h) { return tagList.indexOf(h) !== -1; });
    }

    function fetchResults(q) {
      if (currentXhr) currentXhr.abort && currentXhr.abort();
      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      currentXhr = controller;
      var opts = controller ? { signal: controller.signal } : {};

      var encodedQ = encodeURIComponent(q);

      // Fetch extra results to account for any filtered-out private products
      var fetchLimit = MAX_RESULTS + 4;

      var urlMain = '/search/suggest.json?q=' + encodedQ
        + '&resources[type]=product&resources[limit]=' + fetchLimit
        + '&resources[options][fields]=title,product_type,variants.title,variants.sku,tag';

      // Parallel SKU-only search so exact SKU queries always surface even if
      // the mixed-field search ranks them below title/tag results.
      var urlSku = '/search/suggest.json?q=' + encodedQ
        + '&resources[type]=product&resources[limit]=' + fetchLimit
        + '&resources[options][fields]=variants.sku';

      function safeFetch(url) {
        return fetch(url, opts)
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      }

      Promise.all([safeFetch(urlMain), safeFetch(urlSku)])
        .then(function (responses) {
          if (controller && controller.signal && controller.signal.aborted) return;
          hideSkeleton();

          var mainProducts = (responses[0] && responses[0].resources
            && responses[0].resources.results && responses[0].resources.results.products) || [];
          var skuProducts  = (responses[1] && responses[1].resources
            && responses[1].resources.results && responses[1].resources.results.products) || [];

          // Merge: SKU matches first, then main results, deduplicated by id,
          // with private-tagged products removed client-side.
          var seen = {}, products = [];
          skuProducts.concat(mainProducts).forEach(function (p) {
            if (!seen[p.id] && !isPrivate(p)) { seen[p.id] = true; products.push(p); }
          });
          products = products.slice(0, MAX_RESULTS);

          if (products.length) {
            renderResults(products, q);
          } else {
            fetchDymSuggestion(q);
          }
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          hideSkeleton();
          renderNoResultsFinal(q);
        });
    }

    function renderResults(products, q) {
      if (!resultsGrid) return;
      resultsGrid.innerHTML = products.map(buildCard).join('');
      loadHoverImages(resultsGrid);
      if (resultsQ) resultsQ.textContent = '"' + q + '"';
      if (viewAll) {
        viewAll.href = '/search?type=product&q=' + encodeURIComponent(q);
        viewAll.hidden = false;
      }
      if (noResults) noResults.hidden = true;
      hideDym();
    }

    /* ── DID YOU MEAN — TYPO-CORRECTION FALLBACK ─────────────── */

    // Step 1: ask Shopify's query-suggestion endpoint for a corrected spelling
    function fetchDymSuggestion(q) {
      fetch('/search/suggest.json?q=' + encodeURIComponent(q)
        + '&resources[type]=query&resources[limit]=1')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var queries   = (data.resources && data.resources.results
            && data.resources.results.queries) || [];
          var corrected = queries[0] && queries[0].text;
          if (corrected && corrected.toLowerCase() !== q.toLowerCase()) {
            fetchDymProducts(corrected, q);
          } else {
            renderNoResultsFinal(q);
          }
        })
        .catch(function () { renderNoResultsFinal(q); });
    }

    // Step 2: fetch up to 4 products for the corrected term
    function fetchDymProducts(corrected, originalQ) {
      fetch('/search/suggest.json?q=' + encodeURIComponent(corrected)
        + '&resources[type]=product&resources[limit]=' + (MAX_FALLBACK + 4)
        + '&resources[options][fields]=title,product_type,variants.title,variants.sku,tag')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = ((data.resources && data.resources.results
            && data.resources.results.products) || []).filter(function (p) { return !isPrivate(p); });
          if (products.length) {
            renderDym(products.slice(0, MAX_FALLBACK), corrected, originalQ);
          } else {
            renderNoResultsFinal(originalQ);
          }
        })
        .catch(function () { renderNoResultsFinal(originalQ); });
    }

    // Step 3: render the "did you mean" panel
    function renderDym(products, corrected, originalQ) {
      if (!dymWrapper || !fallbackGrid) { renderNoResultsFinal(originalQ); return; }

      if (dymQueryEl) dymQueryEl.textContent = '"' + originalQ + '"';
      if (dymBtn)     dymBtn.textContent = corrected;

      fallbackGrid.innerHTML = products.map(buildCard).join('');
      loadHoverImages(fallbackGrid);
      dymWrapper.hidden = false;

      if (noResults) noResults.hidden = true;
      if (resultsGrid) resultsGrid.innerHTML = '';
      if (resultsQ) resultsQ.textContent = '"' + originalQ + '"';
      if (viewAll) {
        viewAll.href = '/search?type=product&q=' + encodeURIComponent(corrected);
        viewAll.hidden = false;
      }
    }

    function renderNoResultsFinal(q) {
      if (resultsGrid) resultsGrid.innerHTML = '';
      if (noResults) noResults.hidden = false;
      if (noResultsQ) noResultsQ.textContent = '"' + q + '"';
      if (viewAll) viewAll.hidden = true;
      hideDym();
    }

    // Clicking the corrected-term button re-runs the search with the fixed spelling
    if (dymBtn) {
      dymBtn.addEventListener('click', function () {
        var corrected = dymBtn.textContent;
        if (!corrected) return;
        if (input) input.value = corrected;
        syncClearBtn();
        currentQuery = ''; // reset so onInputFetch doesn't short-circuit on equality
        onInputImmediate();
        updateAutoComplete(corrected);
        fetchResults(corrected);
      });
    }

    /* ── KEYBOARD NAVIGATION ─────────────────────────────────── */

    // Returns the visible autocomplete links (tabindex="-1" links navigated by arrow keys)
    function acLinks() {
      return (!autocompleteEl || autocompleteEl.hidden)
        ? []
        : $$('.csb__autocomplete-item, .csb__autocomplete-view-all', autocompleteEl);
    }

    // Returns the currently active product card elements
    // (fallback grid during DYM state, results grid otherwise)
    function activeCards() {
      if (dymWrapper && !dymWrapper.hidden && fallbackGrid) {
        return $$('.csb__product-card', fallbackGrid);
      }
      return resultsGrid ? $$('.csb__product-card', resultsGrid) : [];
    }

    panel.addEventListener('keydown', function (e) {
      var key    = e.key;
      var active = document.activeElement;

      // Escape from anywhere inside the panel → back to input (panel close is handled globally)
      if (key === 'Escape' && active !== input) {
        e.stopPropagation();
        input && input.focus();
        return;
      }

      if (key !== 'ArrowDown' && key !== 'ArrowUp'
       && key !== 'ArrowLeft' && key !== 'ArrowRight') return;

      var links   = acLinks();
      var cards   = activeCards();
      var acIdx   = links.indexOf(active);
      // Product cards are <a> elements; check closest in case focus is on a child
      var cardEl  = active ? active.closest('.csb__product-card') : null;
      var cardIdx = cardEl ? cards.indexOf(cardEl) : -1;

      /* ── From the search input ───────────────────────────── */
      if (active === input) {
        if (key === 'ArrowDown') {
          e.preventDefault();
          if (links.length)  { links[0].focus();  return; }
          if (cards.length)  { cards[0].focus(); }
        }
        // ArrowUp / Left / Right pass through on the native input
        return;
      }

      /* ── Within the autocomplete dropdown ───────────────── */
      if (acIdx !== -1) {
        e.preventDefault();
        if (key === 'ArrowDown') {
          if (acIdx < links.length - 1)  links[acIdx + 1].focus();
          else if (cards.length)         cards[0].focus();
        } else if (key === 'ArrowUp') {
          if (acIdx > 0)  links[acIdx - 1].focus();
          else            input && input.focus();
        }
        return;
      }

      /* ── Within the product card grid ───────────────────── */
      if (cardIdx !== -1) {
        e.preventDefault();
        var rows   = getGridRows(cards);
        var rowIdx = -1, colIdx = -1;
        rows.forEach(function (row, ri) {
          var ci = row.indexOf(cardEl);
          if (ci !== -1) { rowIdx = ri; colIdx = ci; }
        });

        if (key === 'ArrowRight') {
          if (cardIdx < cards.length - 1) cards[cardIdx + 1].focus();

        } else if (key === 'ArrowLeft') {
          if (cardIdx > 0) cards[cardIdx - 1].focus();

        } else if (key === 'ArrowDown') {
          var nextRow = rows[rowIdx + 1];
          if (nextRow) nextRow[Math.min(colIdx, nextRow.length - 1)].focus();

        } else if (key === 'ArrowUp') {
          if (rowIdx === 0) {
            // First row → go back to autocomplete (last item) or input
            if (links.length) links[links.length - 1].focus();
            else              input && input.focus();
          } else {
            var prevRow = rows[rowIdx - 1];
            prevRow[Math.min(colIdx, prevRow.length - 1)].focus();
          }
        }
        return;
      }
    });

    /* ── HOVER IMAGE LAZY-LOADER ─────────────────────────────── */
    // The Predictive Search API only returns featured_image, not images[].
    // After rendering cards we fetch each product's .js endpoint to inject
    // the second image so the CSS hover cross-fade works.
    function loadHoverImages(grid) {
      if (!grid) return;
      $$('.csb__product-card', grid).forEach(function (card) {
        var wrap = card.querySelector('.csb__product-img-wrap');
        if (!wrap) return;

        var hasPrimary = !!wrap.querySelector('.csb__product-img');
        var hasAlt     = !!wrap.querySelector('.csb__product-img-alt');

        // Nothing to fetch if both images are already in the DOM
        if (hasPrimary && hasAlt) return;

        var href = card.getAttribute('href');
        if (!href || href === '#') return;

        // Strip query/hash so `/products/handle?variant=xxx.js` doesn't 404
        var cleanPath = href.split('?')[0].split('#')[0];
        fetch(cleanPath + '.js')
          .then(function (r) { return r.json(); })
          .then(function (product) {
            if (!product.images || !product.images.length) return;

            // Backfill primary image when Predictive Search returned no featured_image
            if (!wrap.querySelector('.csb__product-img') && product.images[0]) {
              var src0 = product.images[0].src
                .replace(/\.(jpg|png|webp|gif)(\?.*)?$/, '_300x300.$1');
              var img0 = document.createElement('img');
              img0.src     = src0;
              img0.alt     = product.title || '';
              img0.className = 'csb__product-img';
              img0.loading = 'lazy';
              img0.width   = 300;
              img0.height  = 300;
              wrap.insertBefore(img0, wrap.firstChild);
            }

            // Inject second image for CSS hover cross-fade
            if (!wrap.querySelector('.csb__product-img-alt') && product.images[1]) {
              var src1 = product.images[1].src
                .replace(/\.(jpg|png|webp|gif)(\?.*)?$/, '_300x300.$1');
              var img1 = document.createElement('img');
              img1.src     = src1;
              img1.alt     = '';
              img1.className = 'csb__product-img-alt';
              img1.loading = 'lazy';
              img1.width   = 300;
              img1.height  = 300;
              wrap.appendChild(img1);
              card.classList.add('csb__has-alt');
            }
          })
          .catch(function () {});
      });
    }

    /* ── FEATURED PRODUCTS (default / pre-search state) ──────── */

    // Fallback handles shown when no personalization data exists.
    // To update: Shopify Admin → Products → click product → last URL segment is the handle.
    var FEATURED_HANDLES = [
      'the-og-lagoon-tourmaline-signet-ring',
      'aura-sleeve',
      'queen-water-drop-moonstone-necklace-with-full-pave-halo',
      'mini-eau-de-rose-cut-diamond-iris-chain-earrings'
    ];

    // Variant prices arrive in two different units depending on the endpoint:
    //   /products/{handle}.js               → integer cents   (825000)
    //   /collections/{handle}/products.json → dollar string   ("8250.00")
    // buildCard() expects dollar decimals, so convert according to the source.
    function toDollarString(raw, inCents) {
      var num = parseFloat(raw);
      if (isNaN(num) || num <= 0) return null;
      return (inCents ? num / 100 : num).toFixed(2);
    }

    // Normalize a product from /products/{handle}.js (inCents = true) or from
    // /collections/{handle}/products.json (inCents = false) → buildCard() shape
    function normalizeProductJS(p, inCents) {
      if (!p) return null;
      function toImgObj(raw) {
        if (!raw) return null;
        var src = typeof raw === 'string' ? raw : (raw.src || raw.url || null);
        return src ? { src: src, alt: '' } : null;
      }
      var variant = p.variants && p.variants[0];
      return {
        title: p.title,
        handle: p.handle,
        price: variant ? toDollarString(variant.price, inCents) : null,
        compare_at_price_max: variant ? toDollarString(variant.compare_at_price, inCents) : null,
        images: (p.images || []).map(toImgObj).filter(Boolean),
        featured_image: p.featured_image
          ? { url: typeof p.featured_image === 'string' ? p.featured_image : (p.featured_image.src || p.featured_image.url || ''), alt: '' }
          : null,
        tags: p.tags || []
      };
    }

    // Normalize an lh_rv_products entry → buildCard() shape.
    // lh_rv_products stores price as a formatted money string (e.g. "$1,234.00")
    // and images as plain URL strings; buildCard() expects dollar decimals + {src} objects.
    function normalizeRVProduct(p) {
      var priceNum = parseFloat(String(p.price || '').replace(/[^0-9.]/g, ''));
      var imgs = p.images
        ? p.images.map(function (s) { return { src: s, alt: '' }; })
        : (p.image2 ? [{ src: p.image, alt: '' }, { src: p.image2, alt: '' }]
                    : (p.image ? [{ src: p.image, alt: '' }] : []));
      return {
        title: p.name,
        handle: p.handle,
        price: !isNaN(priceNum) && priceNum > 0 ? priceNum.toFixed(2) : null,
        compare_at_price_max: null,
        images: imgs,
        featured_image: p.image ? { url: p.image, alt: p.name || '' } : null,
        tags: p.tags || [],
        url: p.url
      };
    }

    function setFeaturedLabel(text) {
      if (featuredLabel) featuredLabel.textContent = text;
    }

    // Fetch an array of product handles and render them into featuredGrid
    function fetchAndRenderHandles(handles) {
      Promise.all(
        handles.map(function (handle) {
          return fetch('/products/' + handle + '.js')
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (p) { return normalizeProductJS(p, true); })
            .catch(function () { return null; });
        })
      ).then(function (products) {
        var valid = products.filter(Boolean);
        if (!valid.length) return;
        featuredGrid.innerHTML = valid.map(buildCard).join('');
        loadHoverImages(featuredGrid);
      });
    }

    // Fetch products from a collection handle and render them, with fallback to defaults
    function fetchCollectionProducts(handle) {
      fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=4')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          var products = data && data.products
            ? data.products.slice(0, 4)
                .map(function (p) { return normalizeProductJS(p, false); })
                .filter(Boolean)
            : [];
          if (products.length) {
            featuredGrid.innerHTML = products.map(buildCard).join('');
            loadHoverImages(featuredGrid);
          } else {
            setFeaturedLabel('Featured');
            fetchAndRenderHandles(FEATURED_HANDLES);
          }
        })
        .catch(function () {
          setFeaturedLabel('Featured');
          fetchAndRenderHandles(FEATURED_HANDLES);
        });
    }

    // Priority:
    //   1. Ranked handles from LHPersonalization (UTM signals + viewed products) → "For You" / "Recently Viewed"
    //   2. Most recently browsed collection (organic) → "Recently Browsed"
    //   3. Default hardcoded handles → "Featured"
    function loadFeatured() {
      if (!featuredGrid) return;

      try {
      if (window.LHPersonalization) {
        // --- Signal 1: scored recently-viewed + UTM ranked products ---
        var ranked = LHPersonalization.getRankedHandles
          && LHPersonalization.getRankedHandles();
        if (ranked && ranked.length) {
          var rvProducts = LHPersonalization.getRVProducts();
          var rvMap = {};
          rvProducts.forEach(function (p) { rvMap[p.handle] = p; });

          var found = ranked.slice(0, 4).map(function (h) { return rvMap[h]; }).filter(Boolean);

          if (found.length) {
            var signals = LHPersonalization.getSignals();
            setFeaturedLabel(signals.length ? 'For You' : 'Recently Viewed');
            featuredGrid.innerHTML = found.map(normalizeRVProduct).map(buildCard).join('');
            loadHoverImages(featuredGrid);
            return;
          }

          // Ranked handles exist but aren't in rv map — fetch them
          setFeaturedLabel('For You');
          fetchAndRenderHandles(ranked.slice(0, 4));
          return;
        }

        // --- Signal 2: organically browsed collections ---
        // Feature-detected: older LHPersonalization builds have no
        // getViewedCollections method. Calling it blindly throws and would
        // abort loadFeatured before the default fallback runs (empty grid).
        var viewedCollections = LHPersonalization.getViewedCollections
          && LHPersonalization.getViewedCollections();
        if (viewedCollections && viewedCollections.length) {
          setFeaturedLabel('Recently Browsed');
          fetchCollectionProducts(viewedCollections[0].handle);
          return;
        }
      }
      } catch (e) {
        // Any personalization failure → fall through to default featured products
      }

      // --- Signal 3: default hardcoded featured products ---
      fetchAndRenderHandles(FEATURED_HANDLES);
    }

    loadFeatured();
  }

  /* ──────────────────────────────────────────────────────────────
     BOOT
  ────────────────────────────────────────────────────────────── */
  onReady(function () {
    $$('[data-csh-search-panel]').forEach(initSearchPanel);
  });

  document.addEventListener('shopify:section:load', function (e) {
    var panel = e.target && e.target.querySelector('[data-csh-search-panel]');
    if (panel) initSearchPanel(panel);
  });

})();
